import React from 'react';
import {Component} from 'react';
import {observer} from 'mobx-react';
import {observable, makeObservable, action} from 'mobx';
import {
    Button,
    DialogTitle,
    CheckBox,
    HTMLDialog,
    Icon,
    Flex
} from '@priority-software/priority-style-react';
import classNames from 'classnames';

import {messageUIProps} from '../actions/MessageActions';
import {keyCodes} from '../../../constants';
import {
    getTopDialogZindex,
    updateTopDialogZindex
} from '../../../_shared/GWTBridge/invoke/priModalDlg';
import {setHasModalChild} from '../../../_shared/GWTBridge/invoke/netForm';
import {getTryHidingReactMessage} from '../../../_shared/GWTBridge/invoke/genPage';
import {messageType, messagesIds, typeToIcon, typeToPng} from '../consts';
import messageText from '../texts';
import styles from '../styles/Messages.module.scss';
import {MoreInfo} from './MoreInfo';
import {AboutPopup} from './AboutPopup';
import {HyperlinkText} from '../../../components/Common/LinkTexts/LinkTexts';
import {createDataTestId} from '../../../utils/dataTestId';

//return the index of the button we should focus on
const getButtonToFocusIndex = (messageProps: messageUIProps) => {
    const {type, defaultButton} = messageProps;

    //defaultButton - from server - the button we should focus on
    //0 - the seconde button
    if (type !== messageType.confirm) return 0;
    return defaultButton === 0 ? 1 : 0;
};

@observer
class ModalMessage extends Component<any, any> {
    @observable modalOpen: boolean;
    modalMounted = false;
    isProcessing = false;

    initState = () => {
        this.setState({
            dontShowAgain: false,
            focusedRefId: 0
        });
    };

    @action
    setModalOpen = (value: boolean) => {
        this.modalOpen = value;
    };

    showMessage = (messageProps: messageUIProps) => {
        const focusedButtonIndex = getButtonToFocusIndex(messageProps);
        //init elemts refs for focus on keyboard navigation
        this.initRefs(messageProps);
        updateTopDialogZindex(true);
        const defaultState = {
            isRemovable: false,
            size: 'small',
            id: undefined,
            content: undefined,
            onClose: undefined,
            messageIcon: undefined
        };
        const newMessageState = Object.assign(defaultState, messageProps);
        this.setState({messageProps: newMessageState, focusedRefId: focusedButtonIndex}, () => {
            this.isProcessing = false;
            this.setModalOpen(true);
        });
    };

    closeModal = () => {
        this.setModalOpen(false);
    };

    state = {
        dontShowAgain: false,
        focusedRefId: 0,
        messageProps: {} as messageUIProps
    };

    //refs for focus on keyboard arrow navigation
    myRefs = [];
    handleKeyDown = e => {
        if (e.ctrlKey) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        if (e.keyCode === keyCodes.ENTER || e.keyCode === keyCodes.SPACE) {
            const {focusedRefId, messageProps} = this.state;
            const {buttons} = messageProps;
            this.onActionClick(buttons?.[focusedRefId]?.handler);
        } else if (e.keyCode === keyCodes.ESC) {
            this.handleCloseModal();
        } else {
            const ArrowKeys = [
                keyCodes.ARROW_DOWN,
                keyCodes.ARROW_UP,
                keyCodes.ARROW_LEFT,
                keyCodes.ARROW_RIGHT,
                keyCodes.TAB
            ];
            if (ArrowKeys.includes(e.keyCode)) {
                this.handleArrowKeys(e);
            }
        }
    };

    handleArrowKeys = e => {
        const {focusedRefId} = this.state;

        e.preventDefault();
        let nextIdx = 0;
        if (this.myRefs.length === 1) {
            this.myRefs[nextIdx].current.focus();
            return;
        }

        switch (e.keyCode) {
            case keyCodes.ARROW_DOWN:
            case keyCodes.ARROW_LEFT:
            case keyCodes.TAB:
                nextIdx = focusedRefId === 0 ? this.myRefs.length - 1 : focusedRefId - 1;
                break;

            case keyCodes.ARROW_UP:
            case keyCodes.ARROW_RIGHT:
                nextIdx = !this.myRefs[focusedRefId + 1] ? 0 : focusedRefId + 1;
                break;
            default:
                break;
        }

        //update the current index of the element in focus
        this.setState({focusedRefId: nextIdx});

        this.myRefs[nextIdx].current.focus();
    };

    //init all refs for focus on keyboard arrow navigation
    initRefs = props => {
        const refs = [];

        if (props.buttons) {
            props.buttons.forEach((val, i) => {
                refs[i] = React.createRef<HTMLButtonElement>();
            });
        }

        if (props.moreInfo) {
            refs[props.buttons.length] = React.createRef<HTMLButtonElement>();
        }

        this.myRefs = refs;
    };

    handleCloseModal = (hideOnly: boolean = false) => {
        const {closeModal} = this;
        const {
            messageProps: {onClose, id}
        } = this.state;

        //case dont show message again is checked
        this.state.dontShowAgain &&
            this.state.messageProps.isRemovable &&
            localStorage.setItem(id, 'true');

        updateTopDialogZindex(false);
        if (onClose && !hideOnly) {
            onClose();
        }
        closeModal();
        this.initState();
    };

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    async onActionClick(handler) {
        //hasModalChild flag should be false
        //for handler to be run properly;
        //otherwise there're cases
        //when the form stays blocked
        //after warning closing
        if (this.isProcessing) return;

        setHasModalChild(false);
        this.isProcessing = true;
        handler && (await handler());
        this.isProcessing = false;
        if (getTryHidingReactMessage()) {
            this.handleCloseModal(true);
        } else {
            setHasModalChild(true);
        }
    }

    handleChangeCheckboxValue = () => {
        this.setState(prevState => ({dontShowAgain: !prevState.dontShowAgain}));
    };

    renderConfirmButtons = () => {
        const {buttons} = this.state.messageProps;

        const buttonToFocusIndex = getButtonToFocusIndex(this.state.messageProps);
        const ottherbuttonIndex = buttonToFocusIndex === 1 ? 0 : 1;

        return (
            <Flex direction="row-reverse" justifyContent="flex-start" style={{gap: 24}}>
                <Button
                    componentClasses={classNames(styles.footerBtn, 'InlineManual_CancelButton')}
                    inputRef={this.myRefs[buttonToFocusIndex]}
                    skin="primary"
                    name="msgbtn"
                    onClick={() => this.onActionClick(buttons[buttonToFocusIndex].handler)}
                >
                    {buttons[buttonToFocusIndex].text}
                </Button>
                <Button
                    componentClasses={classNames(styles.footerBtn, 'InlineManual_OKButton')}
                    inputRef={this.myRefs[ottherbuttonIndex]}
                    skin="secondary"
                    name="msgbtn"
                    onClick={() => this.onActionClick(buttons[ottherbuttonIndex].handler)}
                >
                    {buttons[ottherbuttonIndex].text}
                </Button>
            </Flex>
        );
    };

    renderMessageButtons = () => {
        const {buttons, type} = this.state.messageProps;

        const {text, handler} = buttons[0];

        if (type === messageType.error && buttons.length > 1) {
            const buttonToFocusIndex = getButtonToFocusIndex(this.state.messageProps);
            const ottherbuttonIndex = buttonToFocusIndex === 1 ? 0 : 1;

            return (
                <Flex direction="row-reverse" justifyContent="flex-start" style={{gap: 24}}>
                    <Button
                        componentClasses={classNames(styles.footerBtn, 'InlineManual_CancelButton')}
                        inputRef={this.myRefs[0]}
                        skin="primary"
                        onClick={() => this.onActionClick(buttons[buttonToFocusIndex].handler)}
                    >
                        {messageText.confirmAndClose}
                    </Button>
                    <Button
                        componentClasses={classNames(styles.footerBtn, 'InlineManual_CancelButton')}
                        inputRef={this.myRefs[ottherbuttonIndex]}
                        skin="secondary"
                        onClick={async () => {
                            await this.onActionClick(buttons[buttonToFocusIndex].handler);
                            this.onActionClick(buttons[ottherbuttonIndex].handler);
                        }}
                    >
                        {messageText.backToProc}
                    </Button>
                </Flex>
            );
        }

        return (
            <Button
                autoFocus
                componentClasses={classNames(styles.footerBtn, 'InlineManual_CancelButton')}
                inputRef={this.myRefs[0]}
                skin="primary"
                onClick={() => this.onActionClick(handler)}
            >
                {text}
            </Button>
        );
    };

    renderButtons() {
        const {type, size, buttons} = this.state.messageProps;

        const classes = classNames(styles.footer, {
            [styles.reverseButtonPos]: type === messageType.info,
            [styles.footerLarge]: size === 'large'
        });

        if (buttons && buttons.length) {
            return (
                //the id is used in gwt Function Init(...)/GeberalPage.java
                <div
                    id="reactModalBtns"
                    className={classes}
                    {...createDataTestId('messages-react-modal-btns')}
                >
                    {type === messageType.confirm
                        ? this.renderConfirmButtons()
                        : this.renderMessageButtons()}
                </div>
            );
        } else {
            return null;
        }
    }

    renderFooter() {
        const {moreInfo, moreInfoLinks} = this.state.messageProps;
        return (
            <div>
                {this.renderButtons()}
                {moreInfo && (
                    <MoreInfo
                        buttonRef={this.myRefs[this.state.messageProps.buttons.length]}
                        text={
                            <HyperlinkText
                                text={moreInfo}
                                linkData={moreInfoLinks}
                                onLinkClick={this.handleOnClickLink}
                            />
                        }
                    />
                )}
            </div>
        );
    }

    handleOnClickLink = linkData => {
        const {onLinkClick} = this.state.messageProps;

        onLinkClick(linkData);
        this.handleCloseModal();
    };

    prepareTitle = () => {
        const {content, title, linkData} = this.state.messageProps;
        return content ? (
            title
        ) : (
            <HyperlinkText text={title} linkData={linkData} onLinkClick={this.handleOnClickLink} />
        );
    };

    renderTitle = () => {
        const {size, content} = this.state.messageProps;
        const title = this.prepareTitle();
        return (
            <DialogTitle
                componentClasses={styles.dialogTitle}
                title={title}
                skin={!content ? 'text' : 'h5'}
                textColor={size === 'large' ? 'primary-500' : 'primary-700'}
            />
        );
    };

    renderContent = () => {
        const {isRemovable, content, linkData, id} = this.state.messageProps;
        if (id === messagesIds.screens.about) {
            const {moreInfoTxt} = this.state.messageProps;
            return <AboutPopup content={content} linkText={moreInfoTxt} />;
        }
        return (
            <>
                <HyperlinkText
                    text={content}
                    linkData={linkData}
                    onLinkClick={this.handleOnClickLink}
                />
                {isRemovable && (
                    <CheckBox
                        checked={this.state.dontShowAgain}
                        onChange={this.handleChangeCheckboxValue}
                    >
                        {messageText.checkbox}
                    </CheckBox>
                )}
            </>
        );
    };

    render() {
        const {modalOpen} = this;
        const {
            messageProps: {size, maskClosable, type, messageIcon, closable = true}
        } = this.state;

        const width = this.state.messageProps.size === 'small' ? 520 : 720;
        const topZindex = getTopDialogZindex();
        const minZindex = 14000;
        const zIndex = topZindex < minZindex ? minZindex : topZindex;

        const icon = messageIcon || typeToIcon[type];
        const imgSrc = typeToPng[type];
        return (
            <div>
                {modalOpen && (
                    <HTMLDialog
                        id="pui-common-message-dialog"
                        width={width}
                        isOpen={modalOpen}
                        size={size}
                        closable={closable}
                        title={this.renderTitle()}
                        toolbarContent={
                            icon ? (
                                <Icon
                                    componentClasses={styles.toolbarIcon}
                                    icon={icon}
                                    iconColor="secondary-500"
                                />
                            ) : imgSrc ? (
                                <img src={imgSrc} height={72} alt="" />
                            ) : null
                        }
                        content={this.renderContent()}
                        footer={this.renderFooter()}
                        maskClosable={!!maskClosable}
                        onClose={this.handleCloseModal}
                        zIndex={zIndex}
                        componentClasses={classNames(
                            styles.messageDialog,
                            'ignore-react-onclickoutside',
                            {[styles.errorMsg]: type === messageType.error}
                        )}
                    />
                )}
            </div>
        );
    }
}

export default ModalMessage;
